import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import {
  listAllEmbodiments,
  listAllTips,
  listOrganizationMembers,
  type MemberRef,
  type Embodiment,
  type Tip,
} from "@/api";
import {
  DatasetReviewController,
  defaultDatasetReviewGateway,
  type DatasetReviewGateway,
  type DatasetReviewSnapshot,
} from "./DatasetReviewController";

/**
 * 데이터셋 리뷰의 데이터 바인딩. 컨트롤러 lifecycle 과 embodiment/tip 카탈로그를 소유한다.
 *
 * 리뷰는 상세 창의 한 탭이다. 그래서 탭을 열기 전에는 조회하지 않는다(`enabled=false`).
 * 컨트롤러 생성은 필드만 보관하므로 비용이 작다. 그래서 컨트롤러는 항상 만들고 `init()` 만
 * 게이팅한다. 그러면 훅 호출 순서가 조건에 따라 바뀌지 않는다.
 *
 * Owns: 컨트롤러 생성·init/dispose·구독, 카탈로그 조회.
 * Does not own: draft 전이(`datasetConfigurationDraft`), 저장 경계(`DatasetReviewController`), 렌더.
 */

// direct 는 상세에서 편집해 검토 요청하거나 저장한다. candidate 는 리뷰 큐에서 편집해 확정하거나
// 되돌린다.
export type DatasetReviewMode = "direct" | "candidate";

export type DatasetReviewBinding = {
  controller: DatasetReviewController;
  snapshot: DatasetReviewSnapshot;
  mode: DatasetReviewMode;
  // 카탈로그. 드롭다운 선택지와 선택된 full Embodiment(상세 카드)를 찾는 데 쓴다. 실패해도 화면은
  // 유지한다.
  embodiments: readonly Embodiment[];
  tips: readonly Tip[];
  members: readonly MemberRef[];
  membersState: "loading" | "loaded" | "error";
  // 아직 탭을 열지 않아 조회하지 않은 상태. 소비부는 이 값으로 빈 화면을 그린다.
  enabled: boolean;
};

export function useDatasetReview(
  datasetUuid: string,
  mode: DatasetReviewMode,
  enabled: boolean,
  gateway: DatasetReviewGateway = defaultDatasetReviewGateway,
): DatasetReviewBinding {
  // 데이터셋이나 mode 가 바뀌면 draft 를 유지할 근거가 없다. 그래서 컨트롤러를 새로 만든다.
  const controller = useMemo(
    () => new DatasetReviewController(datasetUuid, gateway, mode),
    [datasetUuid, gateway, mode],
  );
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    void controller.init();
    return () => controller.dispose();
  }, [controller, enabled]);

  const [members, setMembers] = useState<readonly MemberRef[]>([]);
  const [membersState, setMembersState] = useState<"loading" | "loaded" | "error">("loading");
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setMembersState("loading");
    void listOrganizationMembers()
      .then((list) => {
        if (alive) {
          setMembers(list);
          setMembersState("loaded");
        }
      })
      .catch(() => {
        if (alive) setMembersState("error");
      });
    return () => {
      alive = false;
    };
  }, [enabled]);

  const [embodiments, setEmbodiments] = useState<readonly Embodiment[]>([]);
  const [tips, setTips] = useState<readonly Tip[]>([]);
  useEffect(() => {
    if (!enabled) {
      return;
    }
    let alive = true;
    void Promise.all([listAllEmbodiments(), listAllTips()])
      .then(([embodimentList, tipList]) => {
        if (alive) {
          setEmbodiments(embodimentList);
          setTips(tipList);
        }
      })
      .catch(() => {
        // 카탈로그 로드가 실패하면 드롭다운 선택지만 비고 편집 화면은 유지한다.
      });
    return () => {
      alive = false;
    };
  }, [enabled]);

  return { controller, snapshot, mode, embodiments, tips, members, membersState, enabled };
}
